// src/components/admin/InvoiceGenerator.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { paymentsService } from '../../services/paymentsService';
import { apiRequest } from '../../services/api';
import { Logo } from '../../hooks/useLogo';
import { usePageTitle } from '../../hooks/usePageTitle';
import SearchableSelect from '../common/SearchableSelect';
import '../subscriptions/SubscriptionInvoice.css';
import './InvoiceGenerator.css';

/* ─── helpers ─────────────────────────────────────────────── */
const todayFirstDay = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const todayLastDay = () => {
    const d = new Date();
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${last}`;
};
const fmtDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};
const fmtMoney = (n, cur) => {
    const num = Number(n || 0);
    return `${cur || 'USD'} ${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const statusColor = (s) => {
    switch (s) {
        case 'active':     return '#10b981';
        case 'pending':    return '#f59e0b';
        case 'confirming': return '#3b82f6';
        case 'expired':    return '#ef4444';
        case 'cancelled':  return '#6b7280';
        default:           return '#6b7280';
    }
};

/* ─── single-org invoice block (printable) ─────────────────── */
const OrgInvoice = ({ inv, periodFrom, periodTo, index }) => {
    const { periods, summary, organization_name, contact_email, contact_phone, currency } = inv;
    const cur          = currency || 'USD';
    const hasSetupFee  = summary.total_setup_fee > 0;
    const hasDiscount  = summary.total_discount  > 0;
    const hasTax       = summary.total_tax       > 0;
    const baseColCount = 6;
    const optCount     = (hasSetupFee ? 1 : 0) + (hasDiscount ? 1 : 0) + (hasTax ? 1 : 0);
    const totalCols    = baseColCount + optCount + 2; // +2 for Total + Status
    const labelSpan    = baseColCount + optCount;

    const invoiceNum = periods.length === 1
        ? periods[0].invoice_number
        : `INV-BATCH-${String(inv.organization_id).padStart(4, '0')}`;
    const invoiceDate = periods.length > 0 ? periods[0].invoice_date : new Date().toISOString();

    return (
        <div className={`inv-page inv-wrap${index > 0 ? ' inv-page-break' : ''}`}>

            {/* ── Header ──────────────────────────────────────── */}
            <div className="inv-header">
                <div className="inv-company">
                    <Logo size="large" className="inv-logo" />
                    <div className="inv-company-details">
                        <span>eHAAZRI — Attendance Management System</span>
                        <span>support@ehaazri.com</span>
                    </div>
                </div>
                <div className="inv-meta-block">
                    <table className="inv-meta-table">
                        <tbody>
                            <tr><th>Invoice&nbsp;#</th><td>{invoiceNum}</td></tr>
                            <tr><th>Date</th><td>{fmtDate(invoiceDate)}</td></tr>
                            <tr><th>Period</th><td style={{ whiteSpace: 'nowrap' }}>{fmtDate(periodFrom)} – {fmtDate(periodTo)}</td></tr>
                            <tr><th>Currency</th><td><strong>{cur}</strong></td></tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <hr className="inv-divider" />

            {/* ── Bill-to / Period ──────────────────────────── */}
            <div className="inv-billing-row">
                <div className="inv-bill-section">
                    <div className="inv-section-label">Bill To</div>
                    <div className="inv-org-name">{organization_name || 'Organisation'}</div>
                    <div className="inv-org-id">Organisation ID: {inv.organization_id}</div>
                    {contact_email && <div className="inv-org-id">{contact_email}</div>}
                    {contact_phone && <div className="inv-org-id">{contact_phone}</div>}
                </div>
                <div className="inv-bill-section">
                    <div className="inv-section-label">Billing Period</div>
                    <div className="inv-period-row"><span>From:</span> {fmtDate(periodFrom)}</div>
                    <div className="inv-period-row"><span>To:</span>   {fmtDate(periodTo)}</div>
                    <div className="inv-period-row">
                        <span>Periods:</span>&nbsp;{periods.length} billing period{periods.length !== 1 ? 's' : ''}
                    </div>
                </div>
            </div>

            <hr className="inv-divider" />

            {/* ── Charges table ─────────────────────────────── */}
            <h2 className="inv-section-heading">Charges &amp; Credits</h2>
            <div className="inv-table-wrap">
                <table className="inv-table">
                    <thead>
                        <tr>
                            <th>Description</th>
                            <th className="inv-th-center">Billing Period</th>
                            <th className="inv-th-center">Term</th>
                            <th className="inv-th-right">Unit&nbsp;Rate</th>
                            <th className="inv-th-right">Qty</th>
                            <th className="inv-th-right">Subtotal</th>
                            {hasSetupFee && <th className="inv-th-right">Setup&nbsp;Fee</th>}
                            {hasDiscount && <th className="inv-th-right">Discount</th>}
                            {hasTax      && <th className="inv-th-right">Tax</th>}
                            <th className="inv-th-right">Total</th>
                            <th className="inv-th-center">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {periods.map((p, pi) => (
                            <React.Fragment key={pi}>
                                <tr>
                                    <td>
                                        <strong>{p.staff_count < 5 ? 'Free Tier Subscription' : 'eHAAZRI Attendance Software — Subscription'}</strong>
                                        <br /><small>Attendance tracking — {p.staff_count} staff</small>
                                        <br /><small style={{ color: '#999' }}>{p.invoice_number}</small>
                                    </td>
                                    <td className="inv-td-center" style={{ whiteSpace: 'nowrap', fontSize: '9pt' }}>
                                        {fmtDate(p.period_start)}<br/>
                                        <span style={{ color: '#888' }}>to</span><br/>
                                        {p.period_end ? fmtDate(p.period_end) : '∞ No Expiry'}
                                    </td>
                                    <td className="inv-td-center">
                                        {p.duration_months === 1 ? '1 Month' : `${p.duration_months} Months`}
                                    </td>
                                    <td className="inv-td-right">{fmtMoney(p.unit_price, cur)}</td>
                                    <td className="inv-td-right">{p.staff_count}</td>
                                    <td className="inv-td-right">{fmtMoney(p.subtotal, cur)}</td>
                                    {hasSetupFee && (
                                        <td className="inv-td-right">
                                            {p.setup_fee > 0 ? fmtMoney(p.setup_fee, cur) : '—'}
                                        </td>
                                    )}
                                    {hasDiscount && (
                                        <td className="inv-td-right inv-discount">
                                            {p.discount_amount > 0
                                                ? `− ${fmtMoney(p.discount_amount, cur)}${p.discount_percentage > 0 ? ` (${p.discount_percentage}%)` : ''}`
                                                : '—'}
                                        </td>
                                    )}
                                    {hasTax && (
                                        <td className="inv-td-right inv-tax">
                                            {p.tax_amount > 0
                                                ? `${fmtMoney(p.tax_amount, cur)}${p.tax_percentage > 0 ? ` (${p.tax_percentage}%)` : ''}`
                                                : '—'}
                                        </td>
                                    )}
                                    <td className="inv-td-right"><strong>{fmtMoney(p.total, cur)}</strong></td>
                                    <td className="inv-td-center">
                                        <span className="inv-status-badge" style={{ background: statusColor(p.status) }}>
                                            {(p.status || '').toUpperCase()}
                                        </span>
                                    </td>
                                </tr>

                                {/* Payment reference sub-row */}
                                {p.payment_reference && (
                                    <tr className="inv-payment-ref-row">
                                        <td colSpan={totalCols}>
                                            <i className="fas fa-check-circle" style={{ color: '#10b981', marginRight: 6 }} />
                                            Paid · Ref:&nbsp;<strong>{p.payment_reference}</strong>
                                            {p.payment_date && <> &nbsp;·&nbsp; {fmtDate(p.payment_date)}</>}
                                            {p.payment_gateway && <> &nbsp;·&nbsp; {p.payment_gateway}</>}
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>

                    <tfoot>
                        {/* Subtotal row */}
                        <tr className="inv-subtotal-row">
                            <td colSpan={labelSpan} className="inv-td-label">Subtotal (before adjustments)</td>
                            <td className="inv-td-right">{fmtMoney(summary.total_subtotal, cur)}</td>
                            <td></td>
                        </tr>

                        {/* Setup fee row */}
                        {hasSetupFee && (
                            <tr className="inv-subtotal-row">
                                <td colSpan={labelSpan} className="inv-td-label">One-Time Setup Fee</td>
                                <td className="inv-td-right">{fmtMoney(summary.total_setup_fee, cur)}</td>
                                <td></td>
                            </tr>
                        )}

                        {/* Discount row */}
                        {hasDiscount && (
                            <tr className="inv-discount-row">
                                <td colSpan={labelSpan} className="inv-td-label">Total Discount</td>
                                <td className="inv-td-right inv-discount">− {fmtMoney(summary.total_discount, cur)}</td>
                                <td></td>
                            </tr>
                        )}

                        {/* Tax row */}
                        {hasTax && (
                            <tr className="inv-tax-row">
                                <td colSpan={labelSpan} className="inv-td-label">Total Tax</td>
                                <td className="inv-td-right inv-tax">+ {fmtMoney(summary.total_tax, cur)}</td>
                                <td></td>
                            </tr>
                        )}

                        {/* Grand total */}
                        <tr className="inv-total-row">
                            <td colSpan={labelSpan} className="inv-td-label"><strong>Grand Total</strong></td>
                            <td className="inv-td-right"><strong>{fmtMoney(summary.grand_total, cur)}</strong></td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {/* ── Notes ────────────────────────────────────────── */}
            <div className="inv-notes">
                <h4>Please Note:</h4>
                <ol>
                    <li>This is a system-generated invoice and does not require a signature.</li>
                    <li>For billing inquiries contact <strong>support@ehaazri.com</strong></li>
                    {hasDiscount && <li>Approved discount(s) have been applied to this invoice.</li>}
                    {hasTax      && <li>Tax charges reflect the organisation's jurisdiction as configured by the administrator.</li>}
                </ol>
            </div>
        </div>
    );
};

/* ─── batch summary block ──────────────────────────────────── */
const BatchSummary = ({ invoices, periodFrom, periodTo, generatedAt }) => {
    const totals = invoices.reduce((acc, inv) => {
        acc.subtotal  += inv.summary.total_subtotal;
        acc.setup_fee += inv.summary.total_setup_fee;
        acc.discount  += inv.summary.total_discount;
        acc.tax       += inv.summary.total_tax;
        acc.total     += inv.summary.grand_total;
        return acc;
    }, { subtotal: 0, setup_fee: 0, discount: 0, tax: 0, total: 0 });

    const fmt2 = (n) => n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return (
        <div className="inv-wrap inv-page-break">
            <h2 className="inv-section-heading" style={{ marginTop: 0 }}>
                <i className="fas fa-layer-group" style={{ marginRight: 8, color: '#555' }} />
                Batch Summary — All Organisations
            </h2>
            <p className="inv-gen-batch-meta">
                Period: <strong>{fmtDate(periodFrom)} – {fmtDate(periodTo)}</strong>
                &emsp;·&emsp;Generated: <strong>{generatedAt ? new Date(generatedAt).toLocaleString() : '—'}</strong>
                &emsp;·&emsp;Organisations: <strong>{invoices.length}</strong>
            </p>

            <div className="inv-table-wrap">
                <table className="inv-table">
                    <thead>
                        <tr>
                            <th>Organisation</th>
                            <th className="inv-th-right">Periods</th>
                            <th className="inv-th-right">Subtotal</th>
                            <th className="inv-th-right">Setup&nbsp;Fee</th>
                            <th className="inv-th-right">Discount</th>
                            <th className="inv-th-right">Tax</th>
                            <th className="inv-th-right">Grand&nbsp;Total</th>
                            <th className="inv-th-center">Currency</th>
                        </tr>
                    </thead>
                    <tbody>
                        {invoices.map(inv => (
                            <tr key={inv.organization_id}>
                                <td>
                                    <strong>{inv.organization_name}</strong>
                                    {inv.contact_email && <><br /><small>{inv.contact_email}</small></>}
                                </td>
                                <td className="inv-td-right">{inv.periods.length}</td>
                                <td className="inv-td-right">{fmt2(inv.summary.total_subtotal)}</td>
                                <td className="inv-td-right">
                                    {inv.summary.total_setup_fee > 0 ? fmt2(inv.summary.total_setup_fee) : '—'}
                                </td>
                                <td className="inv-td-right inv-discount">
                                    {inv.summary.total_discount > 0 ? `− ${fmt2(inv.summary.total_discount)}` : '—'}
                                </td>
                                <td className="inv-td-right inv-tax">
                                    {inv.summary.total_tax > 0 ? fmt2(inv.summary.total_tax) : '—'}
                                </td>
                                <td className="inv-td-right"><strong>{fmt2(inv.summary.grand_total)}</strong></td>
                                <td className="inv-td-center">{inv.currency}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr className="inv-total-row">
                            <td className="inv-td-label"><strong>Grand Total</strong></td>
                            <td className="inv-td-right">
                                {invoices.reduce((a, i) => a + i.periods.length, 0)}
                            </td>
                            <td className="inv-td-right">{fmt2(totals.subtotal)}</td>
                            <td className="inv-td-right">
                                {totals.setup_fee > 0 ? fmt2(totals.setup_fee) : '—'}
                            </td>
                            <td className="inv-td-right inv-discount">
                                {totals.discount > 0 ? `− ${fmt2(totals.discount)}` : '—'}
                            </td>
                            <td className="inv-td-right inv-tax">
                                {totals.tax > 0 ? fmt2(totals.tax) : '—'}
                            </td>
                            <td className="inv-td-right"><strong>{fmt2(totals.total)}</strong></td>
                            <td className="inv-td-center">—</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
};

/* ─── main component ───────────────────────────────────────── */
const InvoiceGenerator = () => {
    usePageTitle('Invoice Generator');
    const { user } = useAuth();
    const isCoreAdmin = user?.user_type === 'core_admin';

    // Controls
    const [mode, setMode]               = useState('single'); // 'single' | 'all'
    const [orgId, setOrgId]             = useState(null);
    const [organizations, setOrganizations] = useState([]);
    const [periodFrom, setPeriodFrom]   = useState(todayFirstDay);
    const [periodTo, setPeriodTo]       = useState(todayLastDay);
    const [statusFilter, setStatusFilter] = useState(['active', 'expired']);

    // Results
    const [invoices, setInvoices]       = useState([]);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState(null);

    // Load org list for core admin
    useEffect(() => {
        if (isCoreAdmin) {
            apiRequest.get('/organizations', { params: { limit: 100, status: 2 } })
                .then(r => setOrganizations(r.data.data?.organizations || []))
                .catch(() => {});
        }
    }, [isCoreAdmin]);

    // For client_admin, lock orgId to their own
    useEffect(() => {
        if (!isCoreAdmin && user?.org_id) setOrgId(Number(user.org_id));
    }, [isCoreAdmin, user]);

    const toggleStatus = (s) => {
        setStatusFilter(prev =>
            prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]
        );
    };

    const canGenerate = !loading && statusFilter.length > 0 &&
        (mode === 'all' || orgId != null || !isCoreAdmin);

    const handleGenerate = async () => {
        setLoading(true);
        setError(null);
        setInvoices([]);
        try {
            const params = {
                period_from: periodFrom,
                period_to:   periodTo,
                status:      statusFilter.join(','),
            };
            if (mode === 'single' && (orgId != null || !isCoreAdmin)) {
                params.org_id = orgId ?? user?.org_id;
            }
            const data = await paymentsService.generateInvoice(params);
            setInvoices(data.invoices || []);
            setGeneratedAt(data.generated_at);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        const el = document.getElementById('inv-gen-printable');
        if (!el) { window.print(); return; }

        // Resolve stylesheet hrefs to absolute URLs - a root-relative href like
        // "/static/css/main.css" can fail to load in a window opened with
        // window.open('', ...) depending on how the browser resolves about:blank's
        // base URL, so make sure every href is unambiguous before copying it over.
        const styleNodes = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
            .map(n => {
                if (n.tagName === 'LINK') {
                    const clone = n.cloneNode(true);
                    clone.setAttribute('href', new URL(n.getAttribute('href'), window.location.href).href);
                    return clone.outerHTML;
                }
                return n.outerHTML;
            }).join('\n');

        const win = window.open('', '_blank', 'width=1200,height=860');
        if (!win) { window.print(); return; }
        win.document.write(`<!doctype html><html><head>
            <meta charset="utf-8"/><title>eHAAZRI Invoices</title>
            <base href="${window.location.origin}/" />
            ${styleNodes}
            <style>
                body { margin: 0; padding: 0; background: #fff; }
                .no-print { display: none !important; }
                .inv-gen-controls { display: none !important; }
                @page { size: A4 landscape; margin: 0.3in; }
                @media print {
                    /* This window's body contains only the invoices to print, so
                       undo SubscriptionInvoice.css's single-invoice print rules
                       (body-hiding + position:fixed on .inv-wrap) - with several
                       .inv-wrap blocks here (one per org), "fixed" stacks every
                       one of them on top of each other at the same spot instead
                       of letting them flow one after another with page breaks. */
                    body * { visibility: visible !important; }
                    .inv-wrap {
                        position: static !important;
                        visibility: visible !important;
                        box-shadow: none;
                        border-radius: 0;
                        padding: 0.4in;
                    }
                    .inv-page-break { page-break-before: always; }
                }
            </style>
        </head><body>${el.innerHTML}</body></html>`);
        win.document.close();
        win.focus();

        // Wait for the copied stylesheets to actually finish loading instead of
        // guessing with a fixed delay - on a slow connection (especially the
        // external Font Awesome CDN link) a fixed timeout can fire before the
        // styles are applied, which is what makes the print/PDF look unstyled.
        let printed = false;
        const triggerPrint = () => {
            if (printed) return;
            printed = true;
            win.print();
            win.close();
        };
        win.addEventListener('load', () => setTimeout(triggerPrint, 150));
        setTimeout(triggerPrint, 2000); // safety net if 'load' never fires
    };

    // Grand totals across all invoices
    const grandTotal = invoices.reduce((a, i) => a + i.summary.grand_total, 0);
    const totalPeriods = invoices.reduce((a, i) => a + i.periods.length, 0);

    return (
        <div className="inv-gen-page">

            {/* ── Controls (no-print) ──────────────────────────── */}
            <div className="inv-gen-controls no-print">
                <div className="inv-gen-header">
                    <div className="inv-gen-title">
                        <i className="fas fa-file-invoice-dollar"></i>
                        <h2>Invoice Generator</h2>
                    </div>
                    {generatedAt && (
                        <span className="inv-gen-generated-tag">
                            <i className="fas fa-clock"></i>
                            Generated: {new Date(generatedAt).toLocaleString()}
                        </span>
                    )}
                </div>

                {/* Mode tabs (core admin only) */}
                {isCoreAdmin && (
                    <div className="inv-gen-mode-tabs">
                        <button
                            className={`inv-gen-mode-btn${mode === 'single' ? ' active' : ''}`}
                            onClick={() => setMode('single')}
                        >
                            <i className="fas fa-building"></i> Single Organisation
                        </button>
                        <button
                            className={`inv-gen-mode-btn${mode === 'all' ? ' active' : ''}`}
                            onClick={() => setMode('all')}
                        >
                            <i className="fas fa-layer-group"></i> All Organisations
                        </button>
                    </div>
                )}

                <div className="inv-gen-form">
                    {/* Organisation picker (single mode, core admin only) */}
                    {isCoreAdmin && mode === 'single' && (
                        <div className="inv-gen-field inv-gen-field-wide">
                            <label>Organisation</label>
                            <SearchableSelect
                                value={orgId}
                                onChange={setOrgId}
                                options={organizations}
                                placeholder="— Select Organisation —"
                                displayKey="name"
                                valueKey="id"
                                searchPlaceholder="Search organisations…"
                            />
                        </div>
                    )}

                    {/* Period selectors */}
                    <div className="inv-gen-field">
                        <label>Period From</label>
                        <input
                            type="date"
                            value={periodFrom}
                            onChange={e => setPeriodFrom(e.target.value)}
                            className="inv-gen-input"
                        />
                    </div>
                    <div className="inv-gen-field">
                        <label>Period To</label>
                        <input
                            type="date"
                            value={periodTo}
                            onChange={e => setPeriodTo(e.target.value)}
                            className="inv-gen-input"
                        />
                    </div>

                    {/* Status filter */}
                    <div className="inv-gen-field">
                        <label>Subscription Status</label>
                        <div className="inv-gen-checks">
                            {['active', 'expired', 'cancelled', 'pending'].map(s => (
                                <label key={s} className="inv-gen-check-label">
                                    <input
                                        type="checkbox"
                                        checked={statusFilter.includes(s)}
                                        onChange={() => toggleStatus(s)}
                                    />
                                    <span className="inv-gen-check-dot" style={{ background: statusColor(s) }} />
                                    {s.charAt(0).toUpperCase() + s.slice(1)}
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="inv-gen-actions">
                        <button
                            className="btn btn-primary"
                            onClick={handleGenerate}
                            disabled={!canGenerate}
                        >
                            {loading
                                ? <><i className="fas fa-spinner fa-spin"></i>&nbsp;Generating…</>
                                : <><i className="fas fa-file-invoice"></i>&nbsp;Generate Invoice</>
                            }
                        </button>
                        {invoices.length > 0 && (
                            <button className="btn btn-ghost" onClick={handlePrint}>
                                <i className="fas fa-print"></i>&nbsp;Print / Download PDF
                            </button>
                        )}
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="inv-gen-alert inv-gen-alert-error">
                        <i className="fas fa-exclamation-circle"></i> {error}
                    </div>
                )}

                {/* Empty state */}
                {!loading && invoices.length === 0 && generatedAt && (
                    <div className="inv-gen-empty">
                        <i className="fas fa-search"></i>
                        <p>No subscriptions found for the selected period and filters.</p>
                    </div>
                )}

                {/* Summary bar */}
                {invoices.length > 0 && (
                    <div className="inv-gen-summary-bar">
                        <span>
                            <strong>{invoices.length}</strong>&nbsp;
                            organisation{invoices.length !== 1 ? 's' : ''}
                        </span>
                        <span>
                            <strong>{totalPeriods}</strong>&nbsp;
                            billing period{totalPeriods !== 1 ? 's' : ''}
                        </span>
                        <span className="inv-gen-summary-total">
                            Grand Total:&nbsp;
                            <strong>
                                {invoices[0]?.currency || 'USD'}&nbsp;
                                {grandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </strong>
                        </span>
                    </div>
                )}
            </div>

            {/* ── Printable invoice area ──────────────────────── */}
            {invoices.length > 0 && (
                <div id="inv-gen-printable">
                    {invoices.map((inv, i) => (
                        <OrgInvoice
                            key={inv.organization_id}
                            inv={inv}
                            periodFrom={periodFrom}
                            periodTo={periodTo}
                            index={i}
                        />
                    ))}

                    {/* Batch summary page (only when multiple orgs) */}
                    {invoices.length > 1 && (
                        <BatchSummary
                            invoices={invoices}
                            periodFrom={periodFrom}
                            periodTo={periodTo}
                            generatedAt={generatedAt}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

export default InvoiceGenerator;
